import { useMutation, useQueryClient } from '@tanstack/react-query';
import authAxios from '@services/authAxios';
import { useSnackbarNotification } from '@store/snackbarNotification';
import { useEditDetail } from '../state/useEditDetail';
import { useFileUploadPopUp } from '@store/useFileUploadPopUp';
import { randomStringGenerator } from '@helpers/utils';
import PostUploadModal from '@pages/ConsumerPromotion/components/Detail/PostUploadModal';

export const useMutateDetail = () => {
    const queryClient = useQueryClient();
    const stateEdit = useEditDetail();
    const { onChangeSnackbarNotification } = useSnackbarNotification();
    const { isCancelUpload, set, reset } = useFileUploadPopUp();

    const updateDetailSku = useMutation({
        mutationFn: async ({ id, payload }) => {
            const { data } = await authAxios.patch(`/api/v1/promotion-claim/edit/sku/${id}`, payload);
            return data;
        },
        onSuccess: (data) => {
            stateEdit.set({ isEdit: false });
            queryClient.invalidateQueries('promotion-claim');

            onChangeSnackbarNotification({
                status: 'success',
                description: 'Sukses Menambahkan SKU',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal Menambahkan SKU!',
                duration: 3,
            });
        },
    });

    const uploadFileSkuList = useMutation({
        mutationFn: async ({ promotion_id, file, upload_token, controller }) => {
            set({ isUploading: true });

            const { data } = await authAxios.post(
                `/api/v1/promotion-claim/upload-sku/${promotion_id}`,
                { file: file, upload_token },
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (ev) => {
                        const uploadProgress = Math.round((ev.loaded * 100) / ev.total);
                        set({ uploadProgress });
                    },
                    signal: controller.signal,
                }
            );

            return data;
        },
        onSuccess: ({ data }) => {
            const { count_success, count_update } = data;
            const isSuccess = count_success > 0 || count_update > 0;
            queryClient.invalidateQueries('promotion-claim');
            stateEdit.set({ isEdit: false });

            set({
                isFinishUploading: !isCancelUpload,
                renderContent: () => <PostUploadModal isSuccess={isSuccess} onClose={reset} data={data} />,
            });
        },
        onError: (error) => {
            if (error.name === 'CanceledError') return;
            const errorMessage = error?.response?.data?.message;
            set({
                isFinishUploading: !isCancelUpload,
                renderContent: () => <PostUploadModal isSuccess={false} onClose={reset} errorMessage={errorMessage} />,
            });
        },
        onSettled: () => {
            set({ isUploading: false });
            stateEdit.set({
                upload_token: randomStringGenerator(),
            });
        },
    });

    const cancelUploadFileSkuList = useMutation({
        mutationFn: async ({ promotion_id, upload_token }) => {
            set({
                isCancelUpload: true,
            });
            const { data } = await authAxios.post(`/api/v1/promotion-claim/cancel-upload-sku/${promotion_id}`, {
                upload_token,
            });
            return data;
        },
        onSuccess: (data) => {
            onChangeSnackbarNotification({
                status: 'success',
                description: 'Upload file dibatalkan!',
                duration: 3,
            });
        },

        onSettled: () => {
            set({
                isCancelUpload: false,
            });
        },
    });

    const updateDetailCustomer = useMutation({
        mutationFn: async ({ id, payload }) => {
            const { data } = await authAxios.patch(`/api/v1/promotion-claim/edit/customer/${id}`, payload);
            return data;
        },
        onSuccess: (data) => {
            stateEdit.set({ isEditCustomer: false });
            queryClient.invalidateQueries('promotion-claim');

            onChangeSnackbarNotification({
                status: 'success',
                description: 'Sukses Menambahkan Customer',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal Menambahkan Customer!',
                duration: 3,
            });
        },
    });

    const uploadFileCustomerList = useMutation({
        mutationFn: async ({ promotion_id, file, upload_token, controller }) => {
            set({ isUploading: true });

            const { data } = await authAxios.post(
                `/api/v1/promotion-claim/upload-customer/${promotion_id}`,
                { file: file, upload_token },
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (ev) => {
                        const uploadProgress = Math.round((ev.loaded * 100) / ev.total);
                        set({ uploadProgress });
                    },

                    signal: controller.signal,
                }
            );

            return data;
        },
        onSuccess: (data) => {
            const transformData = {
                count_success: data?.total_success,
                count_update: data?.total_update,
                count_failed: data?.total_failed,
                failed_sku_list: data?.list_customer_failed,
            };
            const { count_success, count_update } = transformData;
            const isSuccess = count_success > 0 || count_update > 0;

            queryClient.invalidateQueries('promotion-claim');
            stateEdit.set({ isEditCustomer: false });
            set({
                isFinishUploading: !isCancelUpload,
                renderContent: () => (
                    <PostUploadModal isSuccess={isSuccess} onClose={reset} data={transformData} isCustomer />
                ),
            });
        },
        onError: (error) => {
            if (error.name === 'CanceledError') return;
            const errorMessage = error?.response?.data?.message;
            set({
                isFinishUploading: !isCancelUpload,
                renderContent: () => <PostUploadModal isSuccess={false} onClose={reset} errorMessage={errorMessage} />,
            });
        },
        onSettled: () => {
            set({ isUploading: false });
            stateEdit.set({
                upload_token: randomStringGenerator(),
            });
        },
    });

    const cancelUploadFileCustomerList = useMutation({
        mutationFn: async ({ promotion_id, upload_token }) => {
            set({
                isCancelUpload: true,
            });
            const { data } = await authAxios.post(`/api/v1/promotion-claim/cancel-upload-customer/${promotion_id}`, {
                upload_token,
            });
            return data;
        },
        onSuccess: (data) => {
            onChangeSnackbarNotification({
                status: 'success',
                description: 'Upload file dibatalkan!',
                duration: 3,
            });
        },

        onSettled: () => {
            set({
                isCancelUpload: false,
            });
        },
    });

    return {
        updateDetailSku,
        uploadFileSkuList,
        cancelUploadFileSkuList,
        updateDetailCustomer,
        uploadFileCustomerList,
        cancelUploadFileCustomerList,
    };
};
